import { useMemo } from "react";

import type { ActivationAlertsContractTrafficType } from "@similarweb/pro-api-client";
import { customRangeFormat } from "@similarweb/pro-logic-components";
import { useTracking } from "@similarweb/tracking-provider";

import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useDurationContext } from "../../../contexts/DurationContext";

export interface ICommonNavigationParams {
    country?: number;
    domain: string;
    trafficType: ActivationAlertsContractTrafficType;
}

const useFormattedMonthlyDuration = () => {
    const duration = useDurationContext();

    return useMemo(
        () => `${duration.raw.from.format(customRangeFormat)}-${duration.raw.to.format(customRangeFormat)}`,
        [duration],
    );
};

export const useKeywordsNavigation = ({
    country = WORLDWIDE_COUNTRY_ID,
    domain,
    trafficType,
}: ICommonNavigationParams) => {
    const duration = useFormattedMonthlyDuration();
    const { track } = useTracking();
    const websitesTrafficKeywordsRouteParams = useMemo(
        () => ({
            key: domain,
            country,
            duration,
            IncludeBranded: false,
            IncludeNoneBranded: true,
            Change: "New",
            ignoreFilterConsistency: true,
            selectedPageTab: trafficType,
            // Explicit webSource avoids webSourceNavChangeHandler redirecting with location:"replace",
            // which would erase the alerts page from history (breaks browser Back).
            webSource: devicesTypes.DESKTOP,
        }),
        [country, domain, duration, trafficType],
    );

    const navigateToKeywordsPage = () => {
        track("Alert Card", "click", "open alert/alerts/keyword");
        void proNavigator.go("organicsearch_websites_v2_traffic_keywords", websitesTrafficKeywordsRouteParams);
    };

    const getKeywordLink = (keyword: string) =>
        proNavigator.href(
            "organicsearch_keywordAnalysis_2",
            {
                keyword,
                country,
                duration,
                selectedPageTab: trafficType,
            },
            { absolute: true },
        );

    const seeMoreKeywordsLink = useMemo(
        () =>
            proNavigator.href("organicsearch_websites_v2_traffic_keywords", websitesTrafficKeywordsRouteParams, {
                absolute: true,
            }),
        [websitesTrafficKeywordsRouteParams],
    );

    return {
        seeMoreKeywordsLink,
        getKeywordLink,
        navigateToKeywordsPage,
    };
};

export const useLandingPagesNavigation = ({
    country = WORLDWIDE_COUNTRY_ID,
    domain,
    trafficType,
}: ICommonNavigationParams) => {
    const duration = useFormattedMonthlyDuration();
    const { track } = useTracking();
    const landingPagesRouteParams = useMemo(
        () => ({
            key: domain,
            country,
            duration,
            Change: "New",
            ignoreFilterConsistency: true,
            selectedPageTab: trafficType,
            // Explicit webSource avoids webSourceNavChangeHandler redirecting with location:"replace",
            // which would erase the alerts page from history (breaks browser Back).
            webSource: devicesTypes.DESKTOP,
        }),
        [country, domain, duration, trafficType],
    );

    const navigateToLandingPage = () => {
        track("Alert Card", "click", "open alert/alerts/landing-page");
        void proNavigator.go("organicsearch_website_landingpages_v2", landingPagesRouteParams);
    };

    const seeMoreLandingPagesLink = useMemo(
        () =>
            proNavigator.href("organicsearch_website_landingpages_v2", landingPagesRouteParams, {
                absolute: true,
            }),
        [landingPagesRouteParams],
    );

    return {
        seeMoreLandingPagesLink,
        navigateToLandingPage,
    };
};
